/* eslint-disable max-len */
import { TitleCasePipe } from '@angular/common';
import { EventEmitter, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DateHelper } from '@bryntum/core-thin';
import { HotToastService } from '@ngneat/hot-toast';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { badgeColorCode } from 'app/components/badge/badge.interface';
import { ModuleUidKey, ParamKey } from 'app/components/notes/notes.interface';
import { DateFormatPipe } from 'app/core/pipes/dateFormat.pipe';
import { TimeFormatPipe } from 'app/core/pipes/timeFormat.pipe';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { NotesService } from 'app/core/services/notes/notes.service';
import { ProjectsService } from 'app/core/services/project/projects.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import dayjs from 'dayjs';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { Observable, Subject } from 'rxjs';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NewPhaseComponent } from '../../new-phase/new-phase.component';
import { DeletePhasePopupComponent } from '../../delete-phase-popup/delete-phase-popup.component';
import { NewMilestoneComponent } from '../../new-milestone/new-milestone.component';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ProjectGanttService {
  updatedProjectDetails: EventEmitter<any> = new EventEmitter<any>();
  updatedDependency: EventEmitter<any> = new EventEmitter<any>();
  ganttViewOptions = [
    {
      label: 'text.day',
      value: 'DAY'
    },
    {
      label: 'calendar.home.tab.week',
      value: 'WEEK'
    },
    {
      label: 'calendar.home.tab.month',
      value: 'MONTH'
    }
  ];
  permissions = { newJob: this.utilityService.hasPermission('job.new'), updateJob: this.utilityService.hasPermission('job.update'), scheduleJob: this.utilityService.hasPermission('job.schedule'), rescheduleJob: this.utilityService.hasPermission('job.reschedule'), deleteJob: this.utilityService.hasPermission('job.delete'), updateJobAssignment: this.utilityService.hasPermission('job.update_assignment'), newNote: this.utilityService.hasPermission('job.new_note'), cloneJob: this.utilityService.hasPermission('job.clone'), updateProject: this.utilityService.hasPermission('project.update') };
  moduleAccess = { map: this.utilityService.hasModuleAccess('MAPS') && this.utilityService.hasPermission('maps.access'), project: this.utilityService.hasModuleAccess('PROJECT') };
  timeFormat = this.utilityService.timeFormat;
  dateFormat = this.utilityService.dateFormat;
  dateTimeFormat = this.utilityService.dateTimeFormat;
  holidays: any[];
  projectJobDetailsUpdateEmitter: Subject<boolean>;
  remoteMetaData: any;
  metaData: any;
  jobColumns = { all: [], checked: [], unChecked: [], default: ['job_title'] };
  defaultMetaData: any = { columns: this.jobColumns.default };
  projectDetails: any;
  constructor(
    private utilityService: UtilityService,
    private dateFormatPipe: DateFormatPipe,
    private titlecasePipe: TitleCasePipe,
    private timeFormatPipe: TimeFormatPipe,
    private toastService: HotToastService,
    private notesService: NotesService,
    private dataCollectorService: DataCollectorService,
    private zuperDialogService: ZuperDialogService,
    private toast: HotToastService,
    private translateService: TranslateService,
    private projectsService: ProjectsService,
    private localStorageService: LocalStorageService,
  ) { }

  getTitle(prefix: string, eventNumber: string, eventTitle: string): string {
    return `#${prefix || ''}${eventNumber || ''} ${eventTitle || ''}`;
  }

  get ganttPresets(): any {
    const presets = {
      'month': [
        {
          id: 'MonthAndCustomSevenDays',
          base: 'dayAndWeek',
          tickWidth: 200,
          headers: [
            {
              unit: 'month',
              dateFormat: 'MMMM YYYY',
              align: 'center'
            },
            {
              unit: 'week',
              increment: 1,
              renderer: (start: Date, end: Date): string => {
                end = moment(end).subtract(1, 'd').toDate();
                if (moment(start).isSame(moment(end), 'd')) {
                  return `${DateHelper.format(start, 'DD')}  ${DateHelper.format(start, 'MMM')}`;
                }
                return `${DateHelper.format(start, 'DD')} - ${DateHelper.format(end, 'DD')}  ${DateHelper.format(start, 'MMM')}`;
              }
            }
          ]
        },
        {
          id: 'hourAndDay',
          base: 'hourAndDay',
          tickWidth: 120,
          headers: [
            {
              unit: 'day',
              renderer: (date: any): string => this.ganttDateRenderer(date, 'ddd ' + this.dateFormat)
            },
            {
              unit: 'hour',
              dateFormat: this.timeFormat
            }
          ]
        }
      ],

      'week': [{
        id: 'dayAndWeek',
        base: 'dayAndWeek',
        tickWidth: 200,
        headers: [
          {
            unit: 'week',
            renderer: (date: any): string => 'Week ' + moment(date).week()
          },
          {
            unit: 'day',
            renderer: (date: any): string => this.ganttDateRenderer(date, 'MM/DD (ddd)')
          }
        ]
      },
      {
        base: 'minuteAndHour',
        id: 'minuteAndHour',
        tickWidth: 120,
        headers: [
          {
            unit: 'hour',
            dateFormat: this.dateFormat + ' ' + this.timeFormat
          },
          {
            unit: 'minutes',
            dateFormat: 'mm',
            increment: 30
          }
        ]
      },
      {
        id: 'hourAndDay',
        base: 'hourAndDay',
        tickWidth: 120,
        headers: [
          {
            unit: 'day',
            renderer: (date: any): string => this.ganttDateRenderer(date, 'ddd ' + this.dateFormat)
          },
          {
            unit: 'hour',
            dateFormat: this.timeFormat
          }
        ]
      }
      ],

      'day': [
        {
          id: 'hourAndDay6hr',
          base: 'hourAndDay',
          tickWidth: 120,
          headers: [
            {
              unit: 'day',
              renderer: (date: any): string => this.ganttDateRenderer(date, 'ddd ' + this.dateFormat)
            },
            {
              unit: 'hour',
              dateFormat: this.timeFormat,
              increment: 6
            }
          ]
        },
        {
          id: 'hourAndDay3hr',
          base: 'hourAndDay',
          tickWidth: 120,
          headers: [
            {
              unit: 'day',
              renderer: (date: any): string => this.ganttDateRenderer(date, 'ddd ' + this.dateFormat)
            },
            {
              unit: 'hour',
              dateFormat: this.timeFormat,
              increment: 3
            }
          ]
        },

        {
          id: 'minuteAndHour',
          base: 'minuteAndHour',
          tickWidth: 120,
          headers: [
            {
              unit: 'day',
              renderer: (date: any): string => this.ganttDateRenderer(date, 'ddd ' + this.dateFormat)
            },
            {
              unit: 'hour',
              dateFormat: this.timeFormat
            },
            {
              unit: 'minutes',
              dateFormat: 'mm',
              increment: 30
            }
          ]
        },
        {
          id: 'hourAndDay',
          base: 'hourAndDay',
          tickWidth: 120,
          headers: [
            {
              unit: 'day',
              renderer: (date: any): string => this.ganttDateRenderer(date, 'ddd ' + this.dateFormat)
            },
            {
              unit: 'hour',
              dateFormat: this.timeFormat,
            }
          ]
        }
      ]
    };
    return presets;
  }

  ganttDateRenderer(date: any, format: any): string {
    const tickDate = moment(date);
    const [isHoliday, holidayName] = this.isConflictWithHolidays(tickDate.clone().startOf('d'), tickDate.clone().endOf('d'), false);
    const rendererHtml = `<div class='flex items-center' ${isHoliday ? `title='${tickDate.format(format)}: ${this.utilityService.getLocaleValue('dispatchBoard.scheduler.holiday.title', 'Holiday')} - ${holidayName}'` : ''}> <span class='b-sch-header-text'>${tickDate.format(format)}</span></div>`;
    return rendererHtml;
  }

  isConflictWithHolidays(startDateTime: Moment, endDateTime: Moment, onlyAllowJobs: boolean): any[] {
    let isHoliday: boolean = false; let holidayNames: string = '';

    if (!this.utilityService.isEmptyOrUndefined(this.holidays)) {
      const holidaysFallsOnDrag: any[] = this.holidays.filter((holiday: any) => this.isDateConflict(startDateTime, endDateTime, moment(holiday.date).startOf('d'), moment(holiday.date).endOf('d')));
      const holidaysWithJobRestriction: any[] = holidaysFallsOnDrag.filter((holiday: any) => !holiday.allow_job || !onlyAllowJobs);
      holidayNames = holidaysWithJobRestriction.length > 0 ? holidaysWithJobRestriction.map((holiday: any) => [holiday.holiday_name + ` (${moment.utc(holiday.date).format(this.dateFormat)})`]).join(', ') : '';
      isHoliday = holidaysWithJobRestriction.length > 0;
    }
    return [isHoliday, holidayNames];
  }

  isDateConflict(startDateTimeOne: any, endDateTimeOne: any, startDateTimeTwo: any, endDateTimeTwo: any): boolean {
    if (moment(startDateTimeTwo).isBetween(moment(startDateTimeOne), moment(endDateTimeOne), undefined, '[)') ||
      moment(endDateTimeTwo).isBetween(moment(startDateTimeOne), moment(endDateTimeOne), undefined, '(]') ||
      (moment(startDateTimeTwo).isBefore(moment(startDateTimeOne)) && moment(endDateTimeTwo).isAfter(moment(endDateTimeOne)))) {
      return true;
    }
    return false;
  }

  getStartEndTime(startDateTime: any, endDateTime: any, isUTC: boolean, format?: string, onlyTime: boolean = false, showEndDate: boolean = false): any[] {
    let startDate: any;
    let endDate: any;
    if (!startDateTime || !endDateTime) { return [null, null]; }
    if (format) {
      startDateTime = isUTC ? dayjs.utc(startDateTime, format) : dayjs(startDateTime, format);
      endDateTime = isUTC ? dayjs.utc(endDateTime, format) : dayjs(endDateTime, format);
    }
    else {
      startDateTime = isUTC ? dayjs.utc(startDateTime) : dayjs(startDateTime);
      endDateTime = isUTC ? dayjs.utc(endDateTime) : dayjs(endDateTime);
    }

    const customFormat = onlyTime ? this.timeFormat : this.dateTimeFormat;
    if (startDateTime.isSame(endDateTime, 'date')) {
      startDate = dayjs(startDateTime).format(customFormat);
      endDate = endDateTime.format(this.timeFormat);
    }
    else {
      startDate = dayjs(startDateTime).format(customFormat);
      if (showEndDate) {
        endDate = dayjs(endDateTime).format(this.dateTimeFormat);
      }
      else {
        endDate = dayjs(endDateTime).format(customFormat);
      }
    }
    return [startDate, endDate];
  }

  getCardColor(jobDetails: any, category: JobColorType): string {
    let colorCode: string = '';
    if (category === 'jobStatus') {
      colorCode = jobDetails.current_job_status?.status_color ?? badgeColorCode[jobDetails.current_job_status?.status_type];
    }
    else if (category === 'jobPriority') {
      const priority = jobDetails?.job_priority;
      colorCode = badgeColorCode[priority] || '#887ECB';
    }
    else if (category === 'jobCategory') {
      colorCode = jobDetails?.job_category?.category_color;
    }
    else if (category === 'team') {
      const assignedTo = jobDetails?.assigned_to;
      if (assignedTo?.length > 0) {
        colorCode = assignedTo[0]?.team?.team_color;
      }
    }
    else if (category === 'none') {
      colorCode = '#ffffff';
    }
    return colorCode;
  }

  validateMentions(noteValue: string): string[] {
    const regex = new RegExp('<a.*?href=.*?\/employees\/([A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12})\/details.*?>', 'g');
    const userMentionsId: string[] = [];
    let matches: any[] = [];
    // eslint-disable-next-line no-cond-assign
    while (matches = regex.exec(noteValue)) {
      userMentionsId.push(matches[1]);
    }
    return userMentionsId;
  }

  createNewNote(value: any): void {
    const payLoad: any = {
      note: {
        attachments: value.attachments && value.attachments.length > 0 ? value.attachments.filter((data: any) => !!data) : [],
        note: value.note,
        job: value.jobUid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        user_mentions: this.validateMentions(value.note), is_private: value.isPrivate, visible_to_customer: value.visibleToCustomer, visible_to_fe: value.isBackOfficeVisible
      },
    };
    if (!_.isEmpty(value.associatedModules)) {
      _.each(_.keys(value.associatedModules), (module: string) => {
        if (module) {
          payLoad.note[ParamKey[module.toUpperCase()]] = value.associatedModules[module]?.length > 0 ? value.associatedModules[module][0][ModuleUidKey[module.toUpperCase()]] : null;
        }
      });
    }
    this.dataCollectorService.postData(this.notesService.notesUrl, payLoad)
      .subscribe(
        (response: any) => {
          this.projectsService.trackEvent('gantt_job_new_note', 'JOBS');
          this.toastService.success(response.message);
        });
  }

  updateJobDetails(payload): Observable<any> {
    const jobUpdateUrl = `${this.dataCollectorService.baseurl()}/jobs`;
    return this.dataCollectorService.putData(jobUpdateUrl, payload);
  }

  detachJob(url): Observable<any> {
    const jobUpdateUrl = `${this.dataCollectorService.baseurl()}/${url}`;
    return this.dataCollectorService.deleteData(jobUpdateUrl);
  }

  projectDetailsChangeConfirmationDialog(projectDetails: any, jobDetails: any, type: 'detach' | 'delete'): MatDialogRef<any> {
    let dialogTitle: string; let dialogMessage: string;
    let confirmLabel: string; let confirmDisplayKey: string;
    let color: string = 'primary';

    if (type === 'detach') {
      dialogTitle = `${this.utilityService.getLocaleValue('text.project.detach.job', 'Detach Job')}`;
      dialogMessage = `${this.utilityService.getLocaleValue('project.detach_job.confirmation')}`;
      confirmLabel = 'Detach';
      confirmDisplayKey = 'text.detach';
      color = 'error';
    }

    const confirmationDialog = this.zuperDialogService.open({
      title: dialogTitle,
      type: 'error',
      message: dialogMessage,
      actions: {
        confirm: {
          show: true,
          label: confirmLabel,
          color: color,
          confirmDisplayKey: confirmDisplayKey,
        },
        cancel: {
          show: true,
          label: 'Cancel',
          cancelDisplayKey: 'dispatchBoard.scheduler.dialog.delete.cancel'
        }
      },
      dismissible: false,
      extraClass: 'job-delete-dialog'
    });
    return confirmationDialog;
  }

  //Templates
  getJobDetailsTemplate(jobDetails: any, view: string): any {
    if (!jobDetails) {
      return '';
    }
    const badgeOption: any = jobDetails.current_job_status ? {
      type: jobDetails.current_job_status?.status_name,
      color: jobDetails.current_job_status?.status_color ?? badgeColorCode[jobDetails.current_job_status?.status_type] ?? null,
      displayName: jobDetails.current_job_status?.status_name,
      extraClass: 'capitalize'
    } : null;
    let template = '';
    const cityAddress: string = this.getCityAddress(jobDetails.customer_address);
    const [scheduledStartDate, scheduledEndDate] = this.getStartEndTime(jobDetails?.scheduled_start_time, jobDetails?.scheduled_end_time, false);
    const [actualStartDate, actualEndDate] = this.getStartEndTime(jobDetails?.actual_start_time, jobDetails?.actual_end_time, false);
   switch (jobDetails?.taskType) {
    case 'JOB':
      template = `<div class="flex flex-col job-preview-panel primary-font w-full">
      <div class="text-base primary-font font-bold flex py-1.5">
          #${jobDetails.prefix ?? ''}${jobDetails.work_order_number} ${jobDetails.job_title}
          ${jobDetails?.route?.route_uid ? `<span class="flex items-center ml-2">
              <em class="ti ti-road text-xl text-blue-700"></em>
          </span>` : ''}
          ${jobDetails.is_recurrence === true ? `<span class="flex items-center ml-2">
              <em class="ti ti-repeat text-xl text-blue-700"></em>
          </span>` : ''}
          ${jobDetails.delayed_job === true ? `<span class="flex items-center ml-2">
              <em class="ti ti-alarm text-xl text-red-700 mr-2"></em>
          </span>` : ''}
      </div>
  
      ${jobDetails.job_category ? `<div class="text-base primary-font flex py-1">
          <span class="flex items-center mr-2">
              <em class="ti ti-category text-xl"></em>
          </span>
          <div class="text-base primary-font flex items-center">
            ${jobDetails.job_category?.category_name}
            ${jobDetails.current_job_status ? `<badge-template class="ml-2" options='${JSON.stringify(badgeOption)}'></badge-template>` : ''}
          </div>
      </div>` : ''}
  
      ${jobDetails.startDate && jobDetails.endDate ? `<div class="text-base primary-font flex py-1">
          <span class="flex mt-0.5 mr-2">
              <em class="ti ti-calendar text-xl"></em>
          </span>
          <div class="text-base primary-font flex flex-wrap items-center">
          ${scheduledStartDate}<em class="ti ti-arrow-narrow-right text-xl mx-1"></em>${scheduledEndDate}
          ${jobDetails.actual_start_time && jobDetails.actual_end_time ? `<span>
              (${this.utilityService.getLocaleValue('job.details.popup.actual_time', 'Actual')} : ${actualStartDate}<em class="ti ti-arrow-narrow-right text-xl mx-1"></em>${actualEndDate})
          </span>` : ''}
          </div>
      </div>` : ''}
  
      ${jobDetails.due_date ? `<div class="text-base primary-font flex py-1">
          <span class="flex items-center mr-2">
              <em class="ti ti-clock-hour-5 text-xl"></em>
          </span>
          ${this.utilityService.getLocaleValue('text.due_by', 'Due By')} : ${this.dateFormatPipe.transform(jobDetails.due_date)}
      </div>` : ''}
  
      ${jobDetails?.project && this.moduleAccess.project ? `<div class="text-base primary-font flex py-1">
          <span class="flex items-center mr-2">
            <em class="ti ti-clipboard-check text-xl"></em>
          </span>
          ${this.utilityService.getLocaleValue('Project', 'Project')} : ${this.getTitle(jobDetails?.project?.project_prefix, jobDetails?.project?.project_number, jobDetails?.project?.project_name)}
      </div>` : ''}
  
      ${jobDetails.customer ? `<div class="text-base primary-font flex py-1">
          <span class="flex items-center mr-2">
              <em class="ti ti-user text-xl"></em>
          </span>
          ${jobDetails.customer.customer_first_name + ' ' + (jobDetails.customer.customer_last_name || '')}
          ${jobDetails.customer.customer_organization ? `<span class="ml-1">(${jobDetails.customer.customer_organization.organization_name})</span>` : ''}
      </div>` : ''}
  
      ${!jobDetails.customer && jobDetails.organization ? `<div class="text-base primary-font flex py-1">
          <span class="flex items-center mr-2">
              <em class="ti ti-building text-xl"></em>
          </span>
          ${jobDetails.organization?.organization_name}
      </div>` : ''}
  
      ${jobDetails.customer_address?.street ? `<div class="text-base primary-font flex py-1">
          <span class="flex mt-0.5 mr-2">
              <em class="ti ti-map-pin text-xl"></em>
          </span>
          ${this.utilityService.getUniqueStreetValue(jobDetails.customer_address)}
      </div>` : ''}
  
      ${cityAddress ? `<div class="text-base primary-font flex py-1">
          <span class="flex mt-0.5 mr-2">
              <em class="ti ti-location text-lg"></em>
          </span>
          ${cityAddress}
      </div>` : ''} 
  
      </div>`;
      break;
    case 'MILESTONE':
      template =   `<div class="flex flex-col job-preview-panel primary-font w-full">
      <div class="text-base primary-font font-bold flex py-1.5">
        <span class="flex items-center mr-2">
              <em class="ti ti-diamonds text-2xl"></em>
          </span>
         ${jobDetails.job_title}</div>
         ${jobDetails.due_date ? `<div class="text-base primary-font flex py-1">
          <span class="flex items-center mr-2">
              <em class="ti ti-clock-hour-5 text-xl"></em>
          </span>
           ${this.utilityService.getLocaleValue('text.due_by', 'Due By')} : ${this.dateFormatPipe.transform(jobDetails.due_date)}
      </div>` : ''}
         </div>`;
        break;
    case 'PHASE':
      template =   `<div class="flex flex-col job-preview-panel primary-font w-full">
      <div class="text-base primary-font font-bold flex py-1.5">
        ${jobDetails.job_title}
         
      </div></div>`;
    break;
   }
   return template;
  }

  getCityAddress(address: any): string {
    const addressArray: string[] = [];
    if (address?.city) {
      addressArray.push(address.city);
    }
    if (address?.state) {
      addressArray.push(address.state);
    }
    if (address?.zip_code) {
      addressArray.push(address.zip_code);
    }
    return addressArray.length > 0 ? addressArray.join(', ') : '';
  }

  getGridCardRenderer(jobDetails: any): string {
    let templateHtml: string = '';
    if (jobDetails) {
      const jobTitle = this.getTitle(jobDetails?.prefix, jobDetails?.work_order_number, jobDetails?.job_title);
      const dueDate = jobDetails?.due_date;
      let duration: any;
      let isOverDue: boolean = false;
      if (dueDate) {
        duration = moment(dueDate).diff(moment(), 'd');
        if (duration < 0) {
          isOverDue = true;
        }
      }
      duration = Math.abs(duration);
      templateHtml += `
      <div class="w-full h-full min-w-72 font-normal text-base primary-font text-gray-700 m-auto mb-2 p-4 bg-white border border-gray-200 cursor-pointer rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
        <div class="w-full h-full">
          <div class="w-1/12 h-full float-left mr-1"><i class="ti ti-grip-horizontal text-2xl"></i></div>
          <div class="w-10/12 card-content float-left">
            <div class="w-full block truncate text-base primary-font font-normal text-gray-700">
              <span>${jobTitle}</span>
            </div>
          <div class="w-full h-full flex items-center gap-2 mt-1">
            <span class='flex items-center text-base primary-font font-normal text-gray-700' title="${this.utilityService.getLocaleValue('due.date.text', 'Due Date')}"><i class="ti ti-clock text-xl mr-1"></i>${this.dateFormatPipe.transform(dueDate)}</span>
            <div class="divider"></div>
            <span class="${isOverDue ? 'text-red-600' : 'text-gray-700'} flex items-center text-base primary-font font-normal" title="${isOverDue ? this.utilityService.getLocaleValue('text.overdue', 'Overdue') : this.utilityService.getLocaleValue('route.label.duration', 'Duration')}"><i class="ti ti-calendar text-xl mr-1"></i>${isOverDue ? this.utilityService.getLocaleValue('text.overdue', 'Overdue') : `${duration} ${duration > 1 ? this.utilityService.getLocaleValue('text.days', 'Days').toLowerCase() : this.utilityService.getLocaleValue('text.day', 'Day').toLowerCase()}`}</span>
          </div>
        </div>
      </div>
      </div>`;
    }
    return templateHtml;
  }

  getGanttCardRenderer(jobDetails: any): string {
    let templateHtml: string = '';
    if (jobDetails) {
        const jobTitle = `${jobDetails.taskType === 'JOB' ? '#' : ''}${jobDetails?.prefix || ''}${jobDetails?.work_order_numbe || ''} ${jobDetails?.job_title || ''}`;
        const phaseClass = jobDetails.taskType === 'JOB' ? 'text-base' : jobDetails.taskType === 'PHASE' ? 'b-phase-pos text-sm' : 'text-sm';
        templateHtml += `<div class="w-full block truncate ${phaseClass} primary-font font-normal text-gray-700">`;
        const milestoneClass = jobDetails.taskType === 'MILESTONE' ? 'b-milestone-pos' : '';
        const isSameDayJob = moment(jobDetails?.scheduled_start_time).isSame(moment(jobDetails?.scheduled_end_time), 'day');
        if (milestoneClass) {
          templateHtml += `<span class=${milestoneClass}>${jobTitle}</span>`;
        } else {
          const jobTitleWidthClass = isSameDayJob ? 'w-3/5' : 'w-1/2';
          templateHtml += `<span class="inline-block ${jobTitleWidthClass} truncate">${jobTitle}</span>`;
        }
        if (jobDetails.taskType === 'MILESTONE' && jobDetails?.milestone_status === 'COMPLETED') {
          templateHtml += '<span class="ti ti-check text-3xl text-white b-milestone-pos-check" title="Completed"></span>';
        }
        if (jobDetails?.scheduled_start_time) {
          const startFormat = 'Do MMM';
          const endFormat = isSameDayJob ? startFormat : 'Do MMM';
          const dateWidthClass = isSameDayJob ? 'w-2/5' : 'w-1/2';
          templateHtml += `<span class='inline-block ${dateWidthClass} float-right truncate text-right'>${DateHelper.format(moment(jobDetails?.scheduled_start_time).toDate(), startFormat)} ${!isSameDayJob ? `- ${DateHelper.format(moment(jobDetails?.scheduled_end_time).toDate(), endFormat)}` : ''} </span>`;
        }
        templateHtml += '</div>';
    }
    return templateHtml;
  }

  ganttColumnRenderer(record: any, column: any): string {
    const jobDetails = record?.record?.data || record;
    const formatedColumnData = this.formatJobDetails(jobDetails, column);
    const fieldType = column?.field_type;
    let columnTemplate = '';
    if (fieldType === 'default_field' || fieldType === 'nested_field') {
      columnTemplate = this.getTableDefaultFieldColumn(column?.data_key, formatedColumnData);
    }
    else if (fieldType === 'custom_field') {
      const decodeDataKey = (column?.data_key || '').split('.');
      if (decodeDataKey.length) {
        const groupId = decodeDataKey[1];
        const label = decodeDataKey[2];
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const customValue = this.getCustomFieldValue(jobDetails.custom_fields, groupId, label);
        if (customValue) {
          columnTemplate = this.getTableCustomFieldColumn(customValue.type, formatedColumnData[column?.data_key]);
        }
      }
    }
    return columnTemplate;
  }

  getCustomFieldValue(customFields: any[], groupId: string, label: string): any {
    if (groupId === 'default') {
      // If the groupId is 'default', no group_name needs to be checked
      return _.find(customFields, { label: label });
    } else {
      // Otherwise, look for the specific group_name and label
      return _.find(customFields, { group_name: groupId, label: label });
    }
  }

  formatJobDetails(jobDetails: any, column: any): any {
    if (!jobDetails) { return; }
    const customData = {};
    customData['id'] = jobDetails?.job_uid;
    customData['is_recurrence'] = jobDetails?.is_recurrence;
    customData['delayed_job'] = jobDetails?.delayed_job;
    if (column.field_type === 'default_field' || column.field_type === 'nested_field') {
      if (column.data_key === 'current_job_status.status_type') {
        if(jobDetails.taskType === 'MILESTONE'){
          if(jobDetails.milestone_status === 'COMPLETED'){
            customData[column.data_key] = { status: jobDetails.milestone_status, color: '#02B875' };
          } else{
            customData[column.data_key] = { status: jobDetails.milestone_status, color: '#887ECB' };
          }
        } else{
          const status = _.get(jobDetails, 'current_job_status.status_name');
          const statusColor = _.get(jobDetails, 'current_job_status.status_color') || '#887ECB';
          if (status) {
            customData[column.data_key] = { status: status, color: statusColor };
          }
        }
      }
      else if (column.data_key === 'scheduled_start_time') {
        const startDateTime = _.get(jobDetails, 'scheduled_start_time');
        const endDateTime = _.get(jobDetails, 'scheduled_end_time');
        let formatedStartDateTime: any;
        let formatedEndDateTime: any;
        if (startDateTime && !endDateTime) {
          formatedStartDateTime = dayjs(startDateTime).utc().format(this.dateTimeFormat);
        }
        else if (startDateTime && endDateTime) {
          [formatedStartDateTime, formatedEndDateTime] = this.getStartEndTime(startDateTime, endDateTime, false);
        }
        if (startDateTime) {
          customData[column.data_key] = {};
          customData[column.data_key]['startDateTime'] = formatedStartDateTime;
          if (endDateTime) {
            customData[column.data_key]['endDateTime'] = formatedEndDateTime;
          }
        }
      }
      else if (column.data_key === 'created_at' || column.data_key === 'updated_at') {
        const dateTime = _.get(jobDetails, column.data_key);
        if (dateTime) {
          customData[column.data_key] = dayjs.utc(dateTime).format(this.dateTimeFormat);
        }
      }
      else if (column.data_key === 'due_date') {
        const dateTime = _.get(jobDetails, column.data_key);
        if (dateTime) {
          customData[column.data_key] = dayjs.utc(dateTime).format(this.dateFormat);
        }
      }
      else if (column.data_key === 'customer') {
        const firstName = _.get(jobDetails, 'customer.customer_first_name');
        const lastName = _.get(jobDetails, 'customer.customer_last_name');
        customData[column.data_key] = this.utilityService.concatTwoStrings(firstName, lastName);
      }
      else if (column.data_key === 'created_by') {
        const firstName = _.get(jobDetails, 'created_by.first_name');
        const lastName = _.get(jobDetails, 'created_by.last_name');
        customData[column.data_key] = this.utilityService.concatTwoStrings(firstName, lastName);
        customData['profilePic'] = _.get(jobDetails, 'created_by.profile_picture');
      }
      else if (column.data_key === 'work_order_skillsets') {
        const skills: string[] = _.get(jobDetails, column.data_key) || [];
        if (skills.length > 0) {
          customData[column.data_key] = {};
          customData[column.data_key]['skill_name'] = skills[0]['skill_name'];
          customData[column.data_key]['skill_count'] = skills.length - 1;
        }
      }
      else if (column.data_key === 'assigned_to') {
        const assignedTo = _.get(jobDetails, 'assigned_to') || [];
        const assignedToTeam = _.get(jobDetails, 'assigned_to_team') || [];
        let totalAssignees = 0;
        customData[column.data_key] = {};
        if (assignedTo.length > 0) {
          customData[column.data_key] = assignedTo;
          totalAssignees = assignedTo.length;
        } else if (assignedTo.length === 0 && assignedToTeam.length > 0) {
          customData[column.data_key] = assignedToTeam;
          totalAssignees = assignedToTeam.length;
        }
        customData[column.data_key]['count'] = totalAssignees - 1;
      }
      else if (column.data_key === 'work_order_number') {
        const prefix = _.get(jobDetails, 'prefix') || '';
        const workOrderNumber = _.get(jobDetails, column.data_key) || '';
        customData[column.data_key] = `${workOrderNumber !== '' ? '#' : ''}${prefix} ${workOrderNumber}`;
      }
      else if (column.data_key === 'route_name') {
        customData[column.data_key] = _.get(jobDetails, column.data_key) ?? '';
        customData['routeDetails']['isLocked'] = jobDetails?.is_locked;
      }
      else if (column.data_key === 'job.customer_address') {
        const location = _.get(jobDetails, 'customer_address') || {};
        const address = _.compact([location?.street, location?.landmark, location?.city || location?.name, location?.state, location?.zip_code, location?.country]).join(', ');
        if (address.length) {
          customData[column.data_key] = address;
        }
      }
      else {
        customData[column.data_key] = _.get(jobDetails, column.data_key) ?? '';
      }
    }
    else if (column.field_type === 'custom_field') {
      const decodeDataKey = (column?.data_key || '').split('.');
      if (decodeDataKey.length) {
        const groupId = decodeDataKey[1];
        const label = decodeDataKey[2];

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const customValue = this.getCustomFieldValue(jobDetails.custom_fields, groupId, label);
        if (customValue) {
          if (customValue.type === 'SINGLE_LINE') {
            if (this.utilityService.isURL(customValue.value)) {
              customData[column.data_key] = {};
              customData[column.data_key]['href'] = customValue.value;
            }
            else {
              customData[column.data_key] = customValue.value;
            }
          }
          else if (customValue.type === 'DATE' && !this.utilityService.isEmptyOrUndefined(customValue.value)) {
            customData[column.data_key] = this.dateFormatPipe.transform(customValue.value);
          }
          else if (customValue.type === 'TIME' && !this.utilityService.isEmptyOrUndefined(customValue.value)) {
            customData[column.data_key] = this.timeFormatPipe.transform(customValue.value, false, 'HH:mm:ss');
          }
          else if (customValue.type === 'DATETIME' && !this.utilityService.isEmptyOrUndefined(customValue.value)) {
            customData[column.data_key] = dayjs.utc(customValue.value, 'YYYY-MM-DD HH:mm:ss').format(this.dateTimeFormat);
          }
          else if (customValue.type === 'FILE' && !this.utilityService.isEmptyOrUndefined(customValue.value)) {
            customData[column.data_key] = {};
            customData[column.data_key]['href'] = customValue.value;
          }
          else if (customValue.type === 'LOOKUP' && !this.utilityService.isEmptyOrUndefined(customValue.value)) {
            customData[column.data_key] = {};
            customData[column.data_key]['value'] = customValue.value;
            customData[column.data_key]['href'] = this.utilityService.getLink(customValue.module_name, customValue.ref_uid);
          }
          else {
            customData[column.data_key] = customValue?.value ? customValue?.value : '';
          }
        }
      }
    }

    return customData;
  }

  getTableDefaultFieldColumn(key: any, value: any): any {
    let rendererHTML: string = '';
    switch (key) {
      case 'work_order_skillsets': {
        rendererHTML = '';
        if (value && value[key]?.skill_name) {
          rendererHTML = `
            <div class="flex gap-1">
            <span class="bg-blue-100 text-blue-800 text-base primary-font font-normal block truncate min-w-8 mr-2 px-2 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300" title="${value[key]?.skill_name}">${value[key]?.skill_name}</span>
            `;
          if (value[key]?.skill_count > 0) {
            rendererHTML += `<span class="bg-blue-100 text-blue-800 text-base primary-font font-normal block truncate min-w-8 mr-2 px-2 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300" title=" ${value[key]?.skill_count} more">+ ${value[key]?.skill_count} more</span>
            </div>`;
          } else {
            rendererHTML += '</div>';
          }
        }
        break;
      };
      case 'current_job_status.status_type': {
        rendererHTML = '';
        if (value && value[key]) {
          const color = value[key]['color'] || badgeColorCode[value[key]];
          const backgroundColor = this.hexToRgba(value[key]['color'], 0.1) || this.hexToRgba(badgeColorCode[value[key]], 0.1);
          rendererHTML = `
            <div class="status-batch text-wrap text-base primary-font font-normal truncate mr-2 px-2 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300" style="background-color: ${backgroundColor}; color: ${color}; border: 1px solid ${color}" title="${value[key]['status']}">${value[key]['status']}</div>`;
        }
        break;
      }
      case 'job_priority': {
        const priorityColor = badgeColorCode[value[key]] || '#ffffff';
        rendererHTML = '';
        if (value && value[key]) {
          value = this.titlecasePipe.transform(value[key]);
          const backgroundColor = this.hexToRgba(priorityColor, 0.1) || this.hexToRgba(priorityColor, 0.1);
          rendererHTML = `
            <div class="status-batch text-wrap text-base primary-font font-normal truncate mr-2 px-2 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300" style="background-color: ${backgroundColor}; color: ${priorityColor}; border: 1px solid ${priorityColor}" title="${value}">${value}</div>`;
        }
        break;
      }
      case 'assigned_to': {
        rendererHTML = '';
        if (value && Array.isArray(value[key]) && value[key].length > 0) {
          const assignedTo = value[key];
          if (assignedTo.length === 1) {
            const item = assignedTo[0];
            if (item.user) {
              const user = item.user;
              if (this.utilityService.hasValidProfilePicture(user?.profile_picture)) {
                rendererHTML = `
                  <div class="assigned-user">
                    <img src="${user?.profile_picture}" title="${user?.first_name} ${user?.last_name}" alt="${user?.first_name} ${user?.last_name}">
                    <span class='block truncate text-base primary-font font-normal'>${user?.first_name} ${user?.last_name}</span>
                  </div>
                `;
              } else {
                const firstLetter = user.first_name.charAt(0).toUpperCase();
                rendererHTML = `
                  <div class="assigned-user">
                    <div class="user-circle font-medium text-base" title="${user.first_name} ${user.last_name}">
                      ${firstLetter}
                    </div>
                    <span class='block truncate text-base primary-font font-normal'>${user?.first_name} ${user?.last_name}</span>
                  </div>
                `;
              }
            } else if (item.team) {
              const team = item.team;
              const firstLetter = team.team_name.charAt(0).toUpperCase();
              rendererHTML = `
                <div class="assigned-user">
                  <div class="team-circle" title="${team.team_name}" style="background-color: ${team.team_color}">
                    ${firstLetter}
                  </div>
                  <span class='block truncate text-base primary-font font-normal'>${team.team_name}</span>
                </div>
              `;
            }
          } else {
            const profiles = assignedTo.slice(0, 3).map((item: any) => {
              if (item.user) {
                if (this.utilityService.hasValidProfilePicture(item.user.profile_picture)) {
                  return `
                    <div class="assigned-user">
                      <img src="${item.user.profile_picture}" title="${item.user.first_name} ${item.user.last_name}" alt="${item.user.first_name} ${item.user.last_name}">
                    </div>
                  `;
                } else {
                  const firstLetter = item.user.first_name.charAt(0).toUpperCase();
                  return `
                    <div class="assigned-user">
                      <div class="user-circle font-medium text-base" title="${item.user.first_name} ${item.user.last_name}">
                        ${firstLetter}
                      </div>
                    </div>
                  `;
                }
              } else if (item.team) {
                const firstLetter = item.team.team_name.charAt(0).toUpperCase();
                return `
                  <div class="assigned-user">
                    <div class="team-circle" title="${item.team.team_name}" style="background-color: ${item.team.team_color}">
                      ${firstLetter}
                    </div>
                  </div>
                `;
              }
              return '';
            });
            if (assignedTo.length > 3) {
              profiles.push(`
                <div class="more-users">
                  <div class="user-circle font-medium text-base">+${assignedTo.length - 3}</div>
                </div>
              `);
            }
            rendererHTML = `<div class="assigned-users">${profiles.join('')}</div>`;
          }
        }
        break;
      }
      case 'work_order_title': {
        rendererHTML = '';
        if (value && value[key]) {
          rendererHTML = `<span class='block truncate font-normal text-base primary-font' title="${value[key]}">${value[key]}</span>`;
          if (value?.delayed_job) {
            rendererHTML += `<em title="${this.utilityService.getLocaleValue('text.delay_job', 'Delay Job')}" class="ti ti-alarm text-2xl text-red-700 mr-2"></em>`;
          }
        }
        break;
      }
      case 'scheduled_start_time': {
        rendererHTML = '';
        if (value && value[key]) {
          const formatedStartDateTime = `${value[key].startDateTime} ${value[key].endDateTime ? `-> ${value[key].endDateTime}` : ''}`;
          rendererHTML = `<div class="flex gap-2 text-base primary-font font-normal text-gray-700 overflow-hidden whitespace-nowrap text-ellipsis" title="${formatedStartDateTime}"><span
            class="block truncate font-normal text-base primary-font">${value[key].startDateTime} ${value[key].endDateTime ? `<i class='ti ti-arrow-narrow-right text-xl mx-1'></i> ${value[key].endDateTime}` : ''}</div>`;
        }
        break;
      }
      case 'created_by': {
        rendererHTML = '';
        if (value && value[key]) {
          const userName = value[key];
          const profilePic = value['profilePic'];
          if (this.utilityService.hasValidProfilePicture(profilePic)) {
            rendererHTML = `
              <div class="assigned-user">
                <img src="${profilePic}" title="${userName}" alt="${userName}">
                <span class='block truncate text-base primary-font font-normal'>${userName}</span>
              </div>
            `;
          } else {
            const firstLetter = userName.charAt(0).toUpperCase();
            rendererHTML = `
              <div class="assigned-user">
                <div class="user-circle font-medium text-base" title="${userName}">
                  ${firstLetter}
                </div>
                <span class='block truncate text-base primary-font font-normal'>${userName}</span>
              </div>
            `;
          }
        }
        break;
      }      
      default: {
        rendererHTML = '';
        if (value && value[key]) {
          rendererHTML = `<span class='text-base primary-font font-normal block truncate' title="${value[key]}">${value[key]}</span>`;
        }
      }
    }
    return rendererHTML;
  }

    // Helper function to convert hex to rgba
  hexToRgba(hex: string, opacity: number): string {
    let r = 0, g = 0, b = 0;
    // 3 digits
    if (hex.length == 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    }
    // 6 digits
    else if (hex.length == 7) {
      r = parseInt(hex[1] + hex[2], 16);
      g = parseInt(hex[3] + hex[4], 16);
      b = parseInt(hex[5] + hex[6], 16);
    }
    return `rgba(${r},${g},${b},${opacity})`;
  }

  getTableCustomFieldColumn(type: any, value: any): any {
    let rendererHTML: string;
    switch (type) {
      case 'SINGLE_LINE':
      case 'TEXT':
        {
          rendererHTML = '';
          if (value && value?.href) {
            rendererHTML = `
            <a class="flex w-10/12 text-justify text-primary-500 hover:underline font-normal text-base primary-font" 
                href="${value?.href}" target="_blank" onclick="event.stopPropagation()">${this.utilityService.getLocaleValue('text.open_link')}</a>`;
          }
          else {
            rendererHTML = `<span title='${value ? value : '-'}'>${value ? value : '-'}</span>`;
          }
          break;
        }

      case 'FILE': {
        rendererHTML = '';
        if (value && value?.href?.length > 1) {
          rendererHTML = `
            <span >
              <a class="flex w-10/12 text-justify text-primary-500 hover:underline font-normal text-base primary-font" 
                          href="${value?.href}" target="_blank" onclick="event.stopPropagation()">${this.utilityService.getLocaleValue('text.open_link')}</a>
            </span>`;
        }
        break;
      }

      case 'LOOKUP': {
        rendererHTML = '';
        if (value?.href) {
          rendererHTML = `
            <span>
            <a class="flex w-10/12 text-justify text-primary-500 hover:underline font-normal text-base primary-font" 
            href="${value?.href}" target="_blank" onclick="event.stopPropagation()">${value?.value}</a>
            </span>`;
        }
        break;
      }

      default: {
        rendererHTML = '';
        if (value) {
          rendererHTML = `<span class='block truncate w-fit font-normal text-base primary-font' title='${value ? value : '-'}'>${value}</span>`;
        }
      }
    }
    return rendererHTML;
  }

  getGanttCurrentTimeTooltip(): string {
    return `<span class="primary-font text-base font-semibold text-gray-700">${this.utilityService.getLocaleValue('text.current_time', 'Current time')}</span>
        <br><span class="primary-font text-base font-medium text-gray-700">${moment().format(this.utilityService.dateTimeFormat)}</span>`;
  }

  getGanttWeekendTooltip(timeRangeData: any): string {
    if (timeRangeData) {
      const startDate = timeRangeData.data.startDate;
      const endDate = timeRangeData.data.endDate;
      return `<span class="primary-font text-base font-semibold text-gray-700">${this.utilityService.getLocaleValue('text.weekend', 'Weekend')}</span>
          <br><span class="primary-font text-base font-medium text-gray-700">${moment(startDate).format(this.utilityService.dateFormat)} - ${moment(endDate).subtract(1, 'h').format(this.utilityService.dateFormat)}</span>`;
    }
  }

  getGanttHolidayTooltip(timeRangeData: any): string {
    if (timeRangeData) {
      const startDate = timeRangeData.data.startDate;
      const holidayName = timeRangeData.data?.holidayName;
      return `<span class="primary-font text-base font-semibold text-gray-700">${this.utilityService.getLocaleValue('dispatchBoard.scheduler.holiday.title', 'Holiday')}</span>
          <br><span class="primary-font text-base font-medium text-gray-700 block truncate" title="${holidayName}">${moment(startDate).format(this.utilityService.dateFormat)} - ${holidayName}</span>`;
    }
  }

  getMetaData(): void {
    const metaDataUrl = `${this.dataCollectorService.baseurl()}/meta_data`;
    const params = {
      module: 'PROJECT',
      key: 'GANTT'
    };
    this.dataCollectorService.getData(metaDataUrl, params).subscribe((response: any) => {
      this.metaData = response['data']?.[0]['value'] ? JSON.parse(response['data']?.[0]['value']) : {};
      this.remoteMetaData = _.cloneDeep(this.metaData);
      if (typeof this.metaData === 'object' && Object.keys(this.metaData).length === 0) {
        this.metaData = this.defaultMetaData;
      }
      this.saveMetaDataLocal();
      this.getJobColumns();

    }, (error: any) => {
      this.getJobColumns();
    });
  }

  getJobColumns(): void {
    const jobColumnUrl = `${this.dataCollectorService.baseurl()}/jobs/meta`;
    this.dataCollectorService.getData(jobColumnUrl).subscribe((response: any) => {
      let allColumns = response?.data || [];
      allColumns = allColumns.filter((column: any) => column.data_key !== "project.project_name" && column.data_key !== "job_title");
      allColumns = allColumns.map((column: any) => _.pick(column, ['display_name', 'display_key', 'data_key', 'field_type']));
      this.jobColumns.all = allColumns;
      this.setJobColumns();
    });
  }

  setJobColumns(columns?: any[]): void {
    const checkedColumnKeys: any[] = columns || this.metaData?.columns || this.jobColumns.default;
    if (this.jobColumns.all.length) {
      this.jobColumns.checked = [];
      this.jobColumns.unChecked = [];
      this.jobColumns.all.forEach((column: any) => {
        if (checkedColumnKeys.includes(column?.data_key)) {
          this.jobColumns.checked.push(column);
        }
        else {
          this.jobColumns.unChecked.push(column);
        }
      });
      this.jobColumns.checked = _.sortBy(this.jobColumns.checked, (column: any) => checkedColumnKeys.indexOf(column?.data_key));
    }
    this.updatedProjectDetails.emit({action: 'COLUMNS'});
  }

  saveMetaData(): void {
    const metaDataUrl = `${this.dataCollectorService.baseurl()}/meta_data`;
    const customizedColumns = this.jobColumns.checked.map((column: any) => column?.data_key);
    const metaData = { columns: customizedColumns };
    const isMetaDataChanged = !_.isEqual(this.remoteMetaData, metaData);
    if (isMetaDataChanged) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const payload = { 'meta_data': { 'module': 'PROJECT', 'key': 'GANTT', 'value': JSON.stringify(metaData) } };
      this.dataCollectorService.postData(metaDataUrl, payload).subscribe((response: any) => {
        this.remoteMetaData = metaData;
        this.saveMetaDataLocal(metaData);
      });
    }
  }

  updateMetaData(key: any, value: any): void {
    if (key) {
      const metaData = this.localStorageService.getLocalStorage('project_gantt_metadata') || this.defaultMetaData;
      metaData[key] = value;
      this.localStorageService.setLocalStorage('project_gantt_metadata', metaData);
      this.metaData = metaData;
    }
  }

  saveMetaDataLocal(metaData: any = this.metaData): void {
    this.localStorageService.setLocalStorage('project_gantt_metadata', metaData);
    this.metaData = metaData;
  }

  refreshMetaData(): void {
    this.metaData = this.localStorageService.getLocalStorage('project_gantt_metadata') || this.defaultMetaData;
  }

  getPhaseByName(phaseName): any {
    const phase = this.projectDetails.project_phases.find((phase: any) => phase.phase_name === phaseName);
    return phase;
  }

  getPhaseById(phaseUid): any {
    const phase = this.projectDetails.project_phases.find((phase: any) => phase.phase_uid === phaseUid);
    return phase;
  }

  editPhase(phaseDetails): void {
    phaseDetails = this.getPhaseByName(phaseDetails.node.key);
    const popupDialog: any = this.zuperDialogService.openCustom(NewPhaseComponent, {
      disableClose: true,
      dismissible: false,
      extraClass: 'project-created-popup',
      hasBackdrop: true,
      project_uid: this.projectDetails?.project_uid,
      phaseDetails:phaseDetails
    });
    popupDialog.afterClosed().subscribe((phaseData: any) => {
      if(phaseData.status){
        phaseData = phaseData.payload;
        this.updatedProjectDetails.emit({action: 'EDIT_PHASE', data: phaseData});
      }
    });
  }

  showMilestoneDeleteWarning(message): void {
    const title = 'text.delete.milestone';
    const dialogType = 'error';
    this.zuperDialogService.open({
      'title': `${this.translateService.instant(title)}`,
      'type': dialogType,
      'message': message,
      'dismissible': true,
      'extraClass':'w-1/3',
      'actions': {
        'cancel': {
          'show': true,
          'label': 'Ok',
          'cancelDisplayKey': 'text.ok'
        }
      }
    });
  }

  validateMilestoneDelete(jobDetails): any{
    const isInvoiced = this.projectDetails?.progressive_invoicing?.invoice_dates?.some(invoice => invoice.is_invoiced === true);
    const milestone = this.projectDetails?.milestones?.find(
      (milestone) => milestone?.milestone_uid === jobDetails?.milestone_uid
    );
    if(!milestone?.progressive_invoice_uid){
      return false;
    }
    if(isInvoiced){
      const message = `${this.translateService.instant('text.project.milestone.delete.warning.progressive.invoice')}`;
      this.showMilestoneDeleteWarning(message);
      return true;
    }
    if(!isInvoiced && milestone?.invoice_amount?.amount === 0){
      return false;
    }
    if(!isInvoiced && milestone?.invoice_amount?.amount > 0){
      const message = `${this.translateService.instant('text.project.milestone.delete.warning.invoice.amount')}`;
      this.showMilestoneDeleteWarning(message);
      return true;
    }
  }

  deleteMilestone(jobDetails): void {
    if(this.validateMilestoneDelete(jobDetails)){
      return;
    }
    const statusType = 'Delete';
    const title = 'text.delete.milestone';
    const message = `${this.translateService.instant('text.milestone.delete')} '${jobDetails?.milestone_title}'?`;
    const confirmDisplayKey = 'delete.text';
    const dialogType = 'error';
    const changeStatus = this.zuperDialogService.open({
      'title': `${this.translateService.instant(title)}`,
      'type': dialogType,
      'message': message,
      'actions': {
        'confirm': {
          'show': true,
          'label': statusType,
          'color': dialogType,
          'confirmDisplayKey': confirmDisplayKey,
        },
        'cancel': {
          'show': true,
          'label': 'Cancel',
          'cancelDisplayKey': 'cancel.text'
        }
      },
      'dismissible': false,
    });
    changeStatus.afterClosed().subscribe((result) => {
      if (result) {
        if (statusType === 'Delete') {
          this.projectsService.deleteMilestone(this.projectDetails.project_uid, jobDetails?.milestone_uid).subscribe((response: any) => {
            this.toast.success(response.message);
            this.updatedProjectDetails.emit({action: 'DELETE_MILESTONE', data: response});
          }, (error: any) => {
          });
        }
      }
    });
  }

  deletePhase(phaseDetails): void {
    phaseDetails = this.getPhaseByName(phaseDetails.node.key);
    const popupDialog: any = this.zuperDialogService.openCustom(DeletePhasePopupComponent, {
      disableClose: true,
      dismissible: false,
      extraClass: 'project-created-popup',
      hasBackdrop: true,
      jobDetails: phaseDetails
    });
    popupDialog.afterClosed().subscribe((result) => {
      if (result) {
        const paramas = { detach: result === '1' ? true : false };
        this.projectsService.deletePhase(this.projectDetails.project_uid, phaseDetails?.phase_uid, paramas).subscribe((response: any) => {
          this.toast.success(response.message);
          this.updatedProjectDetails.emit({action: 'DELETEPHASE',data: response});
        }, (error: any) => {
        });
      }
    });
  }

  editMilestone(milestoneDetails): void {
    const popupDialog: any = this.zuperDialogService.openCustom(NewMilestoneComponent, {
      disableClose: true,
      dismissible: false,
      extraClass: 'project-created-popup',
      hasBackdrop: true,
      projectDetails: this.projectDetails,
      milestoneDetails: milestoneDetails
    });

    popupDialog.afterClosed().subscribe((result: any) => {
      if(result.status){
        const milestoneData = result.payload;
        this.updatedProjectDetails.emit({action: 'EDIT_MILESTONE', data: milestoneData});
      }
    });

  }

  openCreateMilestoneModel(phaseDetails): void{
    const popupDialog: any = this.zuperDialogService.openCustom(NewMilestoneComponent, {
      disableClose: true,
      dismissible: false,
      extraClass: 'project-created-popup',
      hasBackdrop: true,
      projectDetails: this.projectDetails,
      milestoneDetails: this.getPhaseByName(phaseDetails.key)
    });

    popupDialog.afterClosed().subscribe((result: any)=>{
      if(result.status){
        const newMilestone = result.newMilestone;
        this.updatedProjectDetails.emit({action: 'NEWMILESTONE',  data: newMilestone});
      }
    });
  }

  updateMilestoneStatus(milestoneDetails): void{
    const payload = {
      milestone: {
        milestone_status: milestoneDetails.milestone_status === 'OPEN' ? 'COMPLETED' : 'OPEN',
      }
    };
    this.projectsService.updateMilestoneStatus(this.projectDetails.project_uid, milestoneDetails?.milestone_uid, payload).subscribe((response: any) => {
      this.toast.success(response.message);
      this.updatedProjectDetails.emit({action: 'UPDATE_MILESTONE_STATUS', data: payload.milestone});
    }, (error: any) => {
    });
  }

  onColumnCheckChange(column: any, action: 'check' | 'unCheck'): void {
    if (this.jobColumns.default.includes(column?.data_key)) { return; }
    if (action === 'check') {
      this.jobColumns.checked.push(column);
      // eslint-disable-next-line @typescript-eslint/naming-convention
      this.jobColumns.unChecked = _.reject(this.jobColumns.unChecked, { 'data_key': column?.data_key });
    }
    else if (action === 'unCheck') {
      this.jobColumns.unChecked.splice(0, 0, column);
      // eslint-disable-next-line @typescript-eslint/naming-convention
      this.jobColumns.checked = _.reject(this.jobColumns.checked, { 'data_key': column?.data_key });
    }
    this.updatedProjectDetails.emit({action: 'COLUMNS'});
  }

  onColumnDragDrop(event: CdkDragDrop<string[]>): any {
    moveItemInArray(this.jobColumns.checked, event.previousIndex, event.currentIndex);
    this.updatedProjectDetails.emit({action: 'COLUMNS'});
  }

}

export type JobColorType = 'jobStatus' | 'team' | 'jobCategory' | 'jobPriority' | 'none';
